import React from "react";
import { RouteComponentProps } from "react-router";
import { connect } from "react-redux";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import {
  PageHeader,
  PageRoot,
  AttainmentRiskIcon,
  ResponseDueIcon,
  Select,
  TypeaheadSelect
} from "../../components";
import routes from "../../constants/routes";
import { CamStatusReport, AttainmentRisk } from "../../constants/interfaces";
import { dhbActions } from "../../reducers/dhb";
import RxTable, {
  Table,
  IColumn,
  TableDetailCard,
  SearchBar,
  CustomWidget,
  FilterBar
} from "../../components/RxTable";
import formatDate from "../../utils/formatDate";

import {
  attainmentRiskFilterValueTransformFn,
  camStateParse
} from "../audit/CamList";

const styles = (theme: Theme) =>
  createStyles({
    content: {
      padding: "3em"
    },
    description: {
      display: "flex",
      flexDirection: "column"
    },
    detail: {
      marginTop: "2em",
      display: "flex",
      flexWrap: "wrap"
    },
    detailInner: {
      maxWidth: 250,
      marginRight: "2em",
      "&>*": {
        marginBottom: "0.4em"
      }
    },
    subtitle: {
      color: theme.palette.text.secondary
    },
    fullWidthSelect: {
      width: "100%",
      marginBottom: theme.spacing(1)
    },
    select: {
      marginBottom: theme.spacing(1),
      width: "50%"
    },
    toolBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end"
    }
  });

type Props = RouteComponentProps & {
  classes?: any;
  loading: boolean;
  error?: Error;
  getReport: (id: string) => Promise<void>;
  match: any;
  report: CamStatusReport;
  auditStatusMapper: {
    [Key: string]: string;
  };
};

class CamStatusReports extends React.Component<Props> {
  tableColumns: IColumn[];
  constructor(props: Props) {
    super(props);

    this.tableColumns = [
      {
        id: "legalEntityName",
        label: "Legal Entity",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={props.classes.fullWidthSelect}
            id="legalEntityName"
            key="legalEntityName"
            placeholder="Legal entity"
            label="Legal Entity"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "premisesname",
        label: "Premises",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={props.classes.fullWidthSelect}
            id="premisesName"
            key="premisesName"
            placeholder="Premises"
            label="Premises"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "auditDate",
        type: "date",
        label: "Audit Date",
        parse: formatDate.condensed,
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="auditDate"
            key="auditDate"
            label="Audit Date"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "auditType",
        label: "Audit Type",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="auditType"
            key="auditType"
            label="Audit Type"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "criterionId",
        label: "Criterion",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="criterionId"
            key="criterionId"
            label="Criterion"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "responseDueDate",
        type: "date",
        label: "Response Due",
        parse: formatDate.condensed,
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="responseDueDate"
            key="responseDueDate"
            label="Response Due"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  formatDate.condensed(fValue) === formatDate.condensed(cellValue)
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        ),
        renderCell: cell => <ResponseDueIcon dueDate={cell} />
      },
      {
        id: "attainmentRisk",
        label: "Attainment Risk",
        searchable: true,
        parse: attainmentRiskFilterValueTransformFn,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="attainmentRisk"
            key="attainmentRisk"
            label="Attainment Risk"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  attainmentRiskFilterValueTransformFn(fValue) === attainmentRiskFilterValueTransformFn(cellValue)
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        ),
        renderCell: (risk: AttainmentRisk) => <AttainmentRiskIcon risk={risk} />
      },
      {
        id: "camStatus",
        label: "Status",
        searchable: true,
        parse: camStateParse,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="camStatus"
            key="camStatus"
            label="Status"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  camStateParse(fValue) === camStateParse(cellValue)
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      }
    ];
  }

  componentDidMount() {
    const {
      params: { id }
    }: any = this.props.match;
    this.props.getReport(id);
  }

  render() {
    const { loading, classes, report, history } = this.props;
    const cams = report.associatedCams || [];
    return (
      <PageRoot>
        <PageHeader
          breadcrumbLinks={[
            {
              label: routes.HOME.label,
              route: routes.HOME.url
            },
            {
              label: "Audit Status Report",
              route: routes.AUDIT_STATUS_REPORT.url
            }
          ]}
          label={routes.CAM_STATUS_REPORT.label}
        />

        <Paper className={classes.content}>
          <div className={classes.description}>
            <Typography variant="h4">{report.premisesName}</Typography>
            <div className={classes.detail}>
              <div className={classes.detailInner}>
                <Typography className={classes.subtitle} variant="subtitle2">
                  Legal Entity
                </Typography>
                <Typography>{report.legalEntityName}</Typography>
              </div>
              <div className={classes.detailInner}>
                <Typography className={classes.subtitle} variant="subtitle2">
                  Premises
                </Typography>
                <Typography>{report.premisesName}</Typography>
              </div>
              <div className={classes.detailInner}>
                <Typography className={classes.subtitle} variant="subtitle2">
                  Audit Date
                </Typography>
                <Typography>
                  {formatDate.condensed(report.auditDate)}
                </Typography>
              </div>
              <div className={classes.detailInner}>
                <Typography className={classes.subtitle} variant="subtitle2">
                  Audit Status
                </Typography>
                <Typography>{this.props.auditStatusMapper[report.status]}</Typography>
              </div>
            </div>
          </div>

          <RxTable>
            <div className={classes.toolBar}>
              <SearchBar />
              <CustomWidget
                compare={(next, prev) =>
                  next.rawData === prev.rawData && next.filters === prev.filters
                }
              >
                {({ dispatch, filters, columns, rawData }) => (
                  <FilterBar
                    filters={filters}
                    dispatch={dispatch}
                    data={rawData}
                    columns={columns}
                  />
                )}
              </CustomWidget>
            </div>

            <Table
              columns={this.tableColumns}
              loading={loading}
              sorting={{ sortDirection: "asc", id: "responseDueDate" }}
              data={cams}
              rowsPerPage={25}
              getRowId={row => row.caseId}
            />

            <TableDetailCard
              detailCardActions={[
                {
                  label: "View",
                  onClick: cam => {
                    history.push(
                      `${routes.CAM_RESPONSE.url}${cam.caseId}?from=CAM_STATUS_REPORT`,
                      {
                        view: true
                      }
                    );
                  }
                }
              ]}
              columns={this.tableColumns}
            />
          </RxTable>
        </Paper>
      </PageRoot>
    );
  }
}

const mapDispatchToProps = {
  getReport: dhbActions.getCamStatusReport
};

const mapStateToProps = ({ dhb, audit }: any) => {
  return {
    auditStatusMapper: audit.auditStatusMapper,
    error: dhb.error,
    loading: dhb.loading,
    report: dhb.camStatusReport
  };
};

export const styled = withStyles(styles)(CamStatusReports);
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled);
